import { useState } from 'react';
import { ActionIcon, Alert, Button, Checkbox, Grid, Group, NumberInput, Select, Stack, Text, TextInput, Title, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import type { CreateStageRecipeInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../../../auth/permissions';
import { parseNum } from '../../../lib/parseNum';
import { useActiveProducts, useCreateRecipe } from '../api';
import { todayForDateInput } from '../format';

interface InputRow {
  key: string;
  productId: string;
  share: number | string;
}
interface OutputRow {
  key: string;
  productId: string;
  splitPct: number | string;
  expectedMcPct: number | string;
  isPrimary: boolean;
}
interface ConsumableRow {
  key: string;
  productId: string;
  qtyPerOutputTonne: number | string;
}

function newKey(): string {
  return crypto.randomUUID();
}

/** New stage-recipe version form (BUILD_SPEC.md §5.5: "CRUD stages ... and recipes
 * (inputs shares, outputs split%, consumables per output tonne, expected yield)").
 * Every recipe write is a NEW version (§5.5 "one active version per stage") —
 * there is no edit-in-place; `activate` (RecipesPanel.tsx) switches which version
 * is live. */
export function RecipeForm({ stageId, onCreated }: { stageId: string; onCreated: () => void }) {
  const isAdmin = useIsAdmin();
  const productsQuery = useActiveProducts();
  const createRecipe = useCreateRecipe(stageId);
  const products = productsQuery.data?.items ?? [];
  const productOptions = products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }));

  const [expectedYieldDryPct, setExpectedYieldDryPct] = useState<number | string>('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayForDateInput());
  const [inputs, setInputs] = useState<InputRow[]>([{ key: newKey(), productId: '', share: '' }]);
  const [outputs, setOutputs] = useState<OutputRow[]>([
    { key: newKey(), productId: '', splitPct: '', expectedMcPct: '', isPrimary: true },
  ]);
  const [consumables, setConsumables] = useState<ConsumableRow[]>([]);

  const parsedExpectedYieldDryPct = parseNum(expectedYieldDryPct);
  const canSubmit =
    parsedExpectedYieldDryPct !== null &&
    effectiveFrom !== '' &&
    inputs.length > 0 &&
    inputs.every((r) => r.productId && parseNum(r.share) !== null) &&
    outputs.length > 0 &&
    outputs.every((r) => r.productId);

  function handleSubmit() {
    if (!canSubmit || parsedExpectedYieldDryPct === null) return;
    const body: CreateStageRecipeInput = {
      expectedYieldDryPct: parsedExpectedYieldDryPct,
      effectiveFrom: new Date(effectiveFrom),
      inputs: inputs.map((r) => ({ productId: r.productId, share: parseNum(r.share) ?? 0 })),
      outputs: outputs.map((r) => {
        const expectedMcPct = parseNum(r.expectedMcPct);
        return {
          productId: r.productId,
          splitPct: parseNum(r.splitPct),
          expectedMcPct: expectedMcPct === null ? null : expectedMcPct.toFixed(2),
          isPrimary: r.isPrimary,
        };
      }),
      consumables: consumables
        .filter((r) => r.productId && parseNum(r.qtyPerOutputTonne) !== null)
        .map((r) => ({ productId: r.productId, qtyPerOutputTonne: (parseNum(r.qtyPerOutputTonne) as number).toFixed(3) })),
    };
    createRecipe.mutate(body, { onSuccess: onCreated });
  }

  return (
    <Stack gap="md">
      <Group grow>
        <NumberInput
          label="Expected dry yield %"
          decimalScale={2}
          min={0}
          max={100}
          value={expectedYieldDryPct}
          onChange={setExpectedYieldDryPct}
        />
        <TextInput
          label="Effective from"
          type="date"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
        />
      </Group>

      <Title order={5}>Inputs (planning shares)</Title>
      <Text c="dimmed" size="xs">
        The expected in:out ratio — each input's share of the recipe (e.g. 1 for a single-input recipe, or a split across
        several).
      </Text>
      {inputs.map((row, i) => (
        <Grid key={row.key} align="flex-end">
          <Grid.Col span={8}>
            <Select
              label={i === 0 ? 'Product' : undefined}
              placeholder="Product"
              data={productOptions}
              value={row.productId}
              onChange={(v) => setInputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, productId: v ?? '' } : r)))}
              searchable
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <NumberInput
              label={i === 0 ? 'Share' : undefined}
              decimalScale={2}
              min={0}
              value={row.share}
              onChange={(v) => setInputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, share: v } : r)))}
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => setInputs((rows) => rows.filter((r) => r.key !== row.key))}
              disabled={inputs.length <= 1}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      ))}
      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => setInputs((rows) => [...rows, { key: newKey(), productId: '', share: '' }])}
      >
        Add input
      </Button>

      <Title order={5}>Outputs</Title>
      <Text c="dimmed" size="xs">
        What this stage produces. Split % shares the run's cost across multiple outputs where relevant; leave blank to
        split by dry-mass instead.
      </Text>
      {outputs.map((row, i) => (
        <Grid key={row.key} align="flex-end">
          <Grid.Col span={5}>
            <Select
              label={i === 0 ? 'Product' : undefined}
              placeholder="Product"
              data={productOptions}
              value={row.productId}
              onChange={(v) => setOutputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, productId: v ?? '' } : r)))}
              searchable
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <NumberInput
              label={i === 0 ? 'Split %' : undefined}
              decimalScale={1}
              min={0}
              max={100}
              value={row.splitPct}
              onChange={(v) => setOutputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, splitPct: v } : r)))}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <NumberInput
              label={i === 0 ? 'Expected MC%' : undefined}
              decimalScale={2}
              min={0}
              max={100}
              value={row.expectedMcPct}
              onChange={(v) => setOutputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, expectedMcPct: v } : r)))}
            />
          </Grid.Col>
          <Grid.Col span={2}>
            <Checkbox
              mt={i === 0 ? 24 : 0}
              label="Primary"
              checked={row.isPrimary}
              onChange={(e) => setOutputs((rows) => rows.map((r) => (r.key === row.key ? { ...r, isPrimary: e.currentTarget.checked } : r)))}
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => setOutputs((rows) => rows.filter((r) => r.key !== row.key))}
              disabled={outputs.length <= 1}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      ))}
      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => setOutputs((rows) => [...rows, { key: newKey(), productId: '', splitPct: '', expectedMcPct: '', isPrimary: false }])}
      >
        Add output
      </Button>

      <Title order={5}>Consumables (per output tonne)</Title>
      <Text c="dimmed" size="xs">
        The backflush source: how much of each consumable this stage uses per tonne of output. Recorded automatically
        when a run is closed, unless the operator has already entered actual quantities.
      </Text>
      {consumables.map((row, i) => (
        <Grid key={row.key} align="flex-end">
          <Grid.Col span={8}>
            <Select
              label={i === 0 ? 'Product' : undefined}
              placeholder="Product"
              data={productOptions}
              value={row.productId}
              onChange={(v) => setConsumables((rows) => rows.map((r) => (r.key === row.key ? { ...r, productId: v ?? '' } : r)))}
              searchable
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <NumberInput
              label={i === 0 ? 'Qty / output tonne' : undefined}
              decimalScale={3}
              min={0}
              value={row.qtyPerOutputTonne}
              onChange={(v) => setConsumables((rows) => rows.map((r) => (r.key === row.key ? { ...r, qtyPerOutputTonne: v } : r)))}
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <ActionIcon color="red" variant="subtle" onClick={() => setConsumables((rows) => rows.filter((r) => r.key !== row.key))}>
              <IconTrash size={16} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      ))}
      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        onClick={() => setConsumables((rows) => [...rows, { key: newKey(), productId: '', qtyPerOutputTonne: '' }])}
      >
        Add consumable
      </Button>

      {createRecipe.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {createRecipe.error instanceof ApiRequestError ? createRecipe.error.message : 'Failed to create recipe version'}
        </Alert>
      )}
      <Group justify="flex-end">
        <Tooltip label="Requires Admin role" disabled={isAdmin}>
          <span>
            <Button loading={createRecipe.isPending} disabled={!isAdmin || !canSubmit} onClick={handleSubmit}>
              Save new recipe version
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}
